/* ==========================================================================
   Jacoven — Quiet Minimal narrative section
   Section-scoped reveal controller.

   - No global variables (wrapped in IIFE, state kept per-section in a Map).
   - One IntersectionObserver per section instance.
   - Reinitialises on Shopify Theme Editor section:load.
   - Tears down observers + timers on section:unload to avoid leaks/dupes.
   - Mirrors the original behaviour exactly: threshold .12, rootMargin
     '0px 0px -6% 0px', add .jcv-in once, then unobserve; plus a 200ms
     fallback pass that reveals anything already in view.
   ========================================================================== */
(function () {
  'use strict';

  // Per-section instance registry so we never double-bind or leak.
  var instances = new Map();

  function reveal(el) {
    el.classList.add('jcv-in');
  }
  function unreveal(el) {
    el.classList.remove('jcv-in');
  }

  function init(root) {
    if (!root || instances.has(root)) return;

    var inst = { observer: null, timer: null };
    instances.set(root, inst);

    var els = root.querySelectorAll('.jcv-rise');
    if (!els.length) return;

    // Respect a merchant toggle / reduced motion: reveal immediately.
    var animsOff =
      root.classList.contains('jcv--no-anim') ||
      (window.matchMedia &&
        window.matchMedia('(prefers-reduced-motion: reduce)').matches);

    if (animsOff || !('IntersectionObserver' in window)) {
      els.forEach(reveal);
      return;
    }

    // JS is active and animations are on: enable the hidden→reveal pattern.
    root.classList.add('jcv-js');

    // On phones, trigger the reveal earlier (larger bottom margin) so the
    // longer/larger mobile animation plays while the element is entering view,
    // instead of after it's already fully on screen during a fast scroll.
    var isMobile = window.matchMedia && window.matchMedia('(max-width: 600px)').matches;
    // Trigger the reveal a little before the element is fully in view so the
    // rise plays as it enters the comfortable viewing zone (rather than after
    // it's already on screen). Applied on every device for consistent motion.
    var rootMargin = isMobile ? '0px 0px -12% 0px' : '0px 0px -15% 0px';

    // Repeatable reveal: animate every time an element scrolls into view.
    // - When an element intersects, reveal it (plays the rise).
    // - When it has fully left the viewport, reset it to hidden so it can
    //   play again next time it scrolls back in.
    // We do NOT unobserve, so it keeps toggling for the life of the section.
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            reveal(entry.target);
          } else if (entry.intersectionRatio === 0) {
            // Only reset once fully out of view, so it doesn't flicker while
            // partially visible at the edges.
            unreveal(entry.target);
          }
        });
      },
      { threshold: [0, 0.1], rootMargin: rootMargin }
    );

    // Force a paint of the hidden state BEFORE the observer can reveal
    // anything. Without this, above-the-fold elements (common on large desktop
    // viewports) get revealed in the same frame the hidden state is applied,
    // so the browser never animates the transition — they just appear. A
    // double rAF guarantees one painted frame at opacity:0 first.
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        els.forEach(function (el) {
          observer.observe(el);
        });
      });
    });

    // Fallback pass: reveal anything already on screen shortly after load
    // (covers environments where IntersectionObserver callbacks are delayed).
    var timer = window.setTimeout(function () {
      els.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (r.top < window.innerHeight && r.bottom > 0) reveal(el);
      });
    }, 250);

    inst.observer = observer;
    inst.timer = timer;
  }

  function destroy(root) {
    var inst = instances.get(root);
    if (!inst) return;
    if (inst.observer) inst.observer.disconnect();
    if (inst.timer) window.clearTimeout(inst.timer);
    instances.delete(root);
  }

  function initAll(scope) {
    var nodes = (scope || document).querySelectorAll('[data-jcv-section]');
    nodes.forEach(init);
  }

  // Initial boot. This script is loaded with `defer`, so the DOM is already
  // parsed when it runs — initialise straight away. We also re-run on
  // window 'load' as a safety net in case sections are injected late.
  initAll(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  }
  window.addEventListener('load', function () {
    initAll(document);
  });

  // Theme Editor lifecycle — scope to this section's id.
  document.addEventListener('shopify:section:load', function (e) {
    initAll(e.target);
  });
  document.addEventListener('shopify:section:unload', function (e) {
    var root = e.target.querySelector('[data-jcv-section]');
    if (root) destroy(root);
  });

  // Re-run reveal logic when a block is selected/deselected in the editor,
  // so newly added blocks animate in correctly.
  document.addEventListener('shopify:block:select', function (e) {
    var root = e.target.closest('[data-jcv-section]');
    if (root && !instances.has(root)) init(root);
  });
})();